import { useState, useEffect, useRef } from 'react';

export default function WaitlistSignup() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    userType: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.userType) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      if (formData.phone) {
        formBody.append('phone', formData.phone);
      }
      formBody.append('userType', formData.userType);

      const response = await fetch('https://readdy.ai/api/form/d4fi2l465ccm3ui4qs80', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', userType: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="waitlist" ref={sectionRef} className="py-24 bg-gradient-to-br from-teal-900 to-teal-700 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-4xl mx-auto px-6 relative z-10">
        <div
          className={`transition-all duration-1000 transform ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}
        >
          <div className="text-center mb-12">
            <h2 className="text-5xl font-bold text-white mb-4">
              Join the CleanerConnect Waitlist
            </h2>
            <p className="text-xl text-teal-100 mb-3">
              Early access + exclusive beta perks
            </p>
            <p className="text-base text-teal-200 flex items-center justify-center gap-2">
              <i className="ri-shield-check-line text-lg"></i>
              No spam. No commitment. You'll only get early access updates.
            </p>
          </div>

          <form id="waitlist-form" data-readdy-form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
            <div className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-lg font-semibold text-gray-800 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-4 text-lg border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none transition-colors"
                  placeholder="Enter your name"
                  required
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-lg font-semibold text-gray-800 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-4 text-lg border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none transition-colors"
                  placeholder="your@email.com"
                  required
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-lg font-semibold text-gray-800 mb-2">
                  Phone Number <span className="text-gray-400 font-normal">(Optional)</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-4 text-lg border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none transition-colors"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label htmlFor="userType" className="block text-lg font-semibold text-gray-800 mb-2">
                  I am a...
                </label>
                <div className="relative">
                  <select
                    id="userType"
                    name="userType"
                    value={formData.userType}
                    onChange={(e) => setFormData({ ...formData, userType: e.target.value })}
                    className="w-full px-4 py-4 pr-8 text-lg border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none transition-colors appearance-none bg-white cursor-pointer"
                    required
                  >
                    <option value="">Select your role</option>
                    <option value="host">Property Host / Manager</option>
                    <option value="cleaner">Professional Cleaner</option>
                  </select>
                  <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <i className="ri-arrow-down-s-line text-gray-400 text-2xl"></i>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white px-8 py-5 rounded-xl text-xl font-bold transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Joining...' : 'Join Waitlist'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border-2 border-green-500 text-green-800 px-6 py-4 rounded-xl text-center">
                  <i className="ri-checkbox-circle-fill text-2xl mr-2"></i>
                  <strong>Success!</strong> You're on the waitlist. Check your email for updates.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border-2 border-red-500 text-red-800 px-6 py-4 rounded-xl text-center">
                  <i className="ri-error-warning-fill text-2xl mr-2"></i>
                  <strong>Oops!</strong> Please fill in all fields and try again.
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
